// @djsem/ui — Required UI states (Canonical UI Prompt 89).
//
// Every Electron screen must have loading, empty, error, offline, permission-denied, and
// plan-locked states (agent addendum rule 14). These are the canonical primitives so every
// module renders them consistently.

import type { ReactNode, CSSProperties } from "react";
import { tokens } from "./tokens";

const base: CSSProperties = {
  padding: tokens.space(6),
  textAlign: "center",
  color: tokens.color.muted,
  fontFamily: tokens.font.sans,
  border: `1px dashed ${tokens.color.border}`,
  borderRadius: tokens.radius.md,
};

function StateFrame({ icon, title, detail, action, tone }: {
  icon: string; title: string; detail?: ReactNode; action?: ReactNode; tone?: "neutral" | "warn" | "danger";
}) {
  const color = tone === "danger" ? "#e88a8a" : tone === "warn" ? tokens.color.accent : tokens.color.text;
  return (
    <div style={base} role="status">
      <div aria-hidden style={{ fontSize: 22, marginBottom: 8 }}>{icon}</div>
      <div style={{ color, fontSize: 14, marginBottom: 4 }}>{title}</div>
      {detail ? <div style={{ fontSize: 12 }}>{detail}</div> : null}
      {action ? <div style={{ marginTop: 12 }}>{action}</div> : null}
    </div>
  );
}

/**
 * Canonical loading state.
 * @param label - Loading message; defaults to `"Loading…"`.
 */
export function LoadingState({ label = "Loading…" }: { label?: string }) {
  return <StateFrame icon="⏳" title={label} />;
}

/**
 * Canonical empty-resource state.
 * @param title - Heading text.
 * @param detail - Optional supporting detail.
 * @param action - Optional call-to-action content.
 */
export function EmptyState({ title, detail, action }: { title: string; detail?: ReactNode; action?: ReactNode }) {
  return <StateFrame icon="▫" title={title} detail={detail} action={action} />;
}

/**
 * Canonical error state.
 * @param title - Heading text; defaults to `"Something went wrong"`.
 * @param detail - Optional error detail.
 * @param retry - Optional retry control.
 */
export function ErrorState({ title = "Something went wrong", detail, retry }: { title?: string; detail?: ReactNode; retry?: ReactNode }) {
  return <StateFrame icon="⚠" title={title} detail={detail} action={retry} tone="danger" />;
}

/**
 * Canonical offline state.
 * @param detail - Supporting detail; defaults to the standard offline-cache explanation.
 */
export function OfflineState({ detail = "You're offline. Cached data is shown; changes will sync when you reconnect." }: { detail?: ReactNode }) {
  return <StateFrame icon="⛅" title="Offline" detail={detail} tone="warn" />;
}

/**
 * Canonical permission-denied state.
 * @param requiredPermission - Optional permission string to display.
 */
export function PermissionDeniedState({ requiredPermission }: { requiredPermission?: string }) {
  return <StateFrame icon="🔒" title="You don't have access to this area" detail={requiredPermission ? `Requires: ${requiredPermission}` : "Ask a workspace admin for access."} tone="warn" />;
}

/**
 * Canonical plan-locked state (visible-but-gated feature).
 * @param minPlan - The plan tier required to unlock this feature.
 * @param feature - Optional feature name shown in the heading.
 */
export function PlanLockedState({ minPlan, feature }: { minPlan: string; feature?: string }) {
  return <StateFrame icon="★" title={`${feature ?? "This feature"} is available on the ${minPlan} plan`} detail="Upgrade to unlock it. Your work is kept safe either way." tone="warn" />;
}
